"use client";

import { useEffect, useMemo, useState } from "react";

/* "Hang it on your wall" — a CSS room with a sofa for scale, a wall you can
 * recolour, up to three frames you can size, and any picture from the
 * catalog in any frame (search the whole wall-art library from inside the
 * room). No photo assets, no canvas, no animation loop: every object is
 * gradients in %-units so the room scales with the page; frame sizes are
 * real inches against a real 84″ sofa on a 12 ft wall, so 24″×36″ looks as
 * big over the sofa as it will in the buyer's living room. */
export type RoomArt = { slug: string; title: string; cover: string; ratio?: string };

type FrameSize = { key: string; label: string; w: number; h: number };
const SIZES: FrameSize[] = [
  { key: "11x14", label: "11″ × 14″", w: 11, h: 14 },
  { key: "16x20", label: "16″ × 20″", w: 16, h: 20 },
  { key: "18x24", label: "18″ × 24″", w: 18, h: 24 },
  { key: "24x36", label: "24″ × 36″", w: 24, h: 36 },
];
const WALLS = [
  { key: "warm", label: "Warm white", color: "#e9e3d8" },
  { key: "sage", label: "Sage", color: "#b9c4b1" },
  { key: "clay", label: "Clay", color: "#c98d6f" },
  { key: "navy", label: "Navy", color: "#243447" },
  { key: "charcoal", label: "Charcoal", color: "#2d2f33" },
];
const FRAMES = [
  { key: "black", label: "Black", color: "#16171a" },
  { key: "white", label: "White", color: "#f2f0ea" },
  { key: "oak", label: "Oak", color: "#b48a5a" },
];

/* The wall is 10 ft = 120 in wide; the stage is 16:9 so it is 67.5 in tall
 * (the part of the wall above the floor that a camera at sofa height sees).
 * The sofa is 84 in, so 24″×36″ reads at 20% of the stage — the same
 * proportion it has over a real sofa. */
const WALL_IN = 120;
const WALL_H_IN = 67.5;

type Frame = { art: RoomArt; size: FrameSize; x: number; y: number };

function frameFor(art: RoomArt, size: FrameSize, x: number, y: number): Frame {
  /* Landscape art rotates the frame. */
  const landscape = /landscape|5x4|3x2|16x9/i.test(art.ratio ?? "");
  return { art, size: landscape ? { ...size, w: size.h, h: size.w } : size, x, y };
}

export function RoomPreview({
  initial,
  candidates = [],
  heading = "Try it in a room",
}: {
  initial?: RoomArt;
  candidates?: RoomArt[];
  heading?: string;
}) {
  const [pool, setPool] = useState<RoomArt[]>(() => {
    const seen = new Set<string>();
    return [initial, ...candidates].filter((a): a is RoomArt => Boolean(a) && !seen.has(a!.slug) && Boolean(seen.add(a!.slug)));
  });
  const [frames, setFrames] = useState<Frame[]>(() => (initial ? [frameFor(initial, SIZES[1]!, 50, 34)] : []));
  const [selected, setSelected] = useState(0);
  const [wall, setWall] = useState(WALLS[0]!);
  const [frameColor, setFrameColor] = useState(FRAMES[0]!);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<RoomArt[]>([]);
  const [searching, setSearching] = useState(false);

  /* No initial art (department page): pull a first handful from the catalog. */
  useEffect(() => {
    if (initial || pool.length) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetch("/api/catalog?format=wall_art&limit=12");
        const j = (await res.json()) as { items?: Array<{ url: string; title: string; image: string }> };
        const items = (j.items ?? []).map((i) => ({ slug: i.url.split("/").pop() ?? i.url, title: i.title, cover: i.image }));
        if (!alive || !items.length) return;
        setPool(items);
        setFrames([frameFor(items[0]!, SIZES[1]!, 50, 34)]);
      } catch {
        /* leave the room empty — the wall still renders */
      }
    })();
    return () => {
      alive = false;
    };
  }, [initial, pool.length]);

  /* Search the whole library from inside the room. */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(`/api/catalog?format=wall_art&q=${encodeURIComponent(q)}&limit=12`);
          const j = (await res.json()) as { items?: Array<{ url: string; title: string; image: string }> };
          if (!alive) return;
          setResults((j.items ?? []).map((i) => ({ slug: i.url.split("/").pop() ?? i.url, title: i.title, cover: i.image })));
        } catch {
          if (alive) setResults([]);
        } finally {
          if (alive) setSearching(false);
        }
      })();
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query]);

  const current = frames[selected];
  const setSize = (s: FrameSize) => setFrames((f) => f.map((fr, i) => (i === selected ? frameFor(fr.art, s, fr.x, fr.y) : fr)));
  const setArt = (a: RoomArt) => setFrames((f) => f.map((fr, i) => (i === selected ? frameFor(a, fr.size, fr.x, fr.y) : fr)));
  const addFrame = () => {
    if (frames.length >= 3) return;
    const next = pool[(frames.length + 1) % Math.max(1, pool.length)] ?? pool[0];
    if (!next) return;
    const xs = [50, 24, 76];
    setFrames((f) => [...f, frameFor(next, SIZES[0]!, xs[f.length] ?? 50, 36)]);
    setSelected(frames.length);
  };
  const removeFrame = () => {
    if (frames.length <= 1) return;
    setFrames((f) => f.filter((_, i) => i !== selected));
    setSelected(0);
  };
  const shuffle = () => {
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)]!;
    setArt(pick);
  };

  const [buying, setBuying] = useState(false);
  const [buyMsg, setBuyMsg] = useState<string | null>(null);
  const distinctSlugs = useMemo(() => Array.from(new Set(frames.map((f) => f.art.slug))), [frames]);
  const buyWall = async () => {
    if (buying || distinctSlugs.length < 2) return;
    setBuying(true);
    setBuyMsg(null);
    try {
      const res = await fetch("/api/shop/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: distinctSlugs.map((slug) => ({ kind: "product", slug })),
          bundle: "gallery-wall",
          cancelPath: typeof window !== "undefined" ? window.location.pathname : "/digital-art",
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error(data.error || "Checkout unavailable.");
    } catch (e) {
      setBuyMsg(e instanceof Error ? e.message : "Checkout unavailable.");
      setBuying(false);
    }
  };

  const floorTop = 72; // % of stage height where the wall meets the floor
  const inToPctW = (inches: number) => (inches / WALL_IN) * 100;
  const inToPctH = (inches: number) => (inches / WALL_H_IN) * floorTop;
  const wallIsDark = useMemo(() => ["navy", "charcoal"].includes(wall.key), [wall.key]);

  return (
    <div className="rounded-3xl border p-4 md:p-6" style={{ borderColor: "var(--color-border-default)", background: "var(--color-bg-elev-1)" }}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--color-accent)" }}>{heading}</div>
          <p className="mt-1 text-[14px] text-white/70">Real inches over a real 84″ sofa. Change the wall, the frame, the size — or hang any picture in the library.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {WALLS.map((w) => (
            <button key={w.key} type="button" aria-label={`${w.label} wall`} title={w.label} onClick={() => setWall(w)} className="h-7 w-7 rounded-full border-2" style={{ background: w.color, borderColor: w.key === wall.key ? "var(--color-accent)" : "rgba(255,255,255,.15)" }} />
          ))}
        </div>
      </div>

      {/* The stage — 16:9 room. */}
      <div className="relative mt-5 w-full overflow-hidden rounded-2xl" style={{ aspectRatio: "16 / 9", background: wall.color }}>
        {/* Wall light falloff */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(90% 70% at 50% 20%, rgba(255,255,255,.18), rgba(0,0,0,.18) 90%)" }} />
        {/* Baseboard + floor */}
        <div className="absolute left-0 right-0" style={{ top: `${floorTop - 1.5}%`, height: "1.5%", background: wallIsDark ? "#1a1b1f" : "#ffffff", opacity: 0.9 }} />
        <div className="absolute left-0 right-0 bottom-0" style={{ top: `${floorTop}%`, background: "repeating-linear-gradient(90deg,#8a6a4a 0 6%,#946f4c 6% 12%,#7f6045 12% 18%)" }} />
        <div className="absolute left-0 right-0 bottom-0" style={{ top: `${floorTop}%`, background: "linear-gradient(180deg, rgba(0,0,0,.25), rgba(0,0,0,0) 40%)" }} />
        {/* Sofa: 84 in wide, seat at floor line */}
        <div className="absolute" style={{ left: `${50 - inToPctW(84) / 2}%`, width: `${inToPctW(84)}%`, top: `${floorTop - 22}%`, height: "22%" }}>
          <div className="absolute" style={{ left: "0", right: "0", top: "0", height: "46%", borderRadius: "10% 10% 2% 2%", background: "linear-gradient(180deg,#5b6b7c,#465563)" }} />
          <div className="absolute" style={{ left: "3%", right: "3%", top: "40%", height: "36%", borderRadius: "6%", background: "linear-gradient(180deg,#6b7b8c,#4f5d6b)", boxShadow: "inset 0 -6px 12px rgba(0,0,0,.25)" }} />
          <div className="absolute" style={{ left: "6%", width: "42%", top: "18%", height: "30%", borderRadius: "8%", background: "linear-gradient(180deg,#7a8a9b,#5c6a79)" }} />
          <div className="absolute" style={{ right: "6%", width: "42%", top: "18%", height: "30%", borderRadius: "8%", background: "linear-gradient(180deg,#7a8a9b,#5c6a79)" }} />
          <div className="absolute" style={{ left: "-2%", width: "8%", top: "0", height: "80%", borderRadius: "40% 40% 10% 10%", background: "linear-gradient(90deg,#4a5866,#5f6e7d)" }} />
          <div className="absolute" style={{ right: "-2%", width: "8%", top: "0", height: "80%", borderRadius: "40% 40% 10% 10%", background: "linear-gradient(90deg,#5f6e7d,#4a5866)" }} />
          <div className="absolute" style={{ left: "4%", width: "2%", top: "78%", height: "22%", background: "#2b2622" }} />
          <div className="absolute" style={{ right: "4%", width: "2%", top: "78%", height: "22%", background: "#2b2622" }} />
        </div>
        {/* Plant on the right */}
        <div className="absolute" style={{ left: "85%", width: "6%", top: `${floorTop - 26}%`, height: "26%" }}>
          <div className="absolute" style={{ left: "20%", width: "60%", bottom: "0", height: "34%", borderRadius: "10% 10% 20% 20%", background: "linear-gradient(180deg,#c9b8a3,#a89786)" }} />
          <div className="absolute" style={{ left: "-30%", width: "160%", top: "-10%", height: "76%", borderRadius: "50% 50% 40% 40%", background: "radial-gradient(circle at 40% 35%, #6fa36d, #3f6b45 70%)" }} />
        </div>
        {/* Lamp on the left */}
        <div className="absolute" style={{ left: "12%", width: "5%", top: `${floorTop - 40}%`, height: "40%" }}>
          <div className="absolute" style={{ left: "46%", width: "8%", top: "18%", bottom: "2%", background: "#3a3a3f" }} />
          <div className="absolute" style={{ left: "0", right: "0", top: "0", height: "22%", borderRadius: "10% 10% 45% 45%", background: "linear-gradient(180deg,#f0e6cf,#d9caa8)", boxShadow: "0 30px 40px -10px rgba(255,235,190,.35)" }} />
          <div className="absolute" style={{ left: "10%", right: "10%", bottom: "0", height: "3%", borderRadius: "50%", background: "#2c2c30" }} />
        </div>

        {/* Frames */}
        {frames.map((fr, i) => {
          const w = inToPctW(fr.size.w);
          const h = inToPctH(fr.size.h);
          const on = i === selected;
          return (
            <button
              key={`${fr.art.slug}-${i}`}
              type="button"
              aria-label={`${fr.art.title}, ${fr.size.label}${on ? " (selected)" : ""}`}
              onClick={() => setSelected(i)}
              className="absolute transition-all duration-300 motion-reduce:transition-none"
              style={{ left: `${fr.x - w / 2}%`, top: `${fr.y - h / 2}%`, width: `${w}%`, height: `${h}%`, background: frameColor.color, padding: "1.6%", boxShadow: `0 ${h / 8}% ${h / 4}% -${h / 10}% rgba(0,0,0,.55), inset 0 0 0 1px rgba(255,255,255,.12)`, outline: on ? "2px solid var(--color-accent)" : "none", outlineOffset: "3px" }}
            >
              <div className="h-full w-full" style={{ background: "#faf8f3", padding: "6%" }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={fr.art.cover} alt="" className="h-full w-full" style={{ objectFit: "cover", display: "block" }} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/65">Size · selected frame</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {SIZES.map((s) => {
              const on = current?.size.key === s.key;
              return (
                <button key={s.key} type="button" onClick={() => setSize(s)} className="rounded-full border px-3 py-1.5 font-mono text-[11px]" style={{ borderColor: on ? "var(--color-accent)" : "var(--color-border-default)", color: on ? "var(--color-accent)" : "rgba(255,255,255,.75)" }}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/65">Frame</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {FRAMES.map((f) => {
              const on = f.key === frameColor.key;
              return (
                <button key={f.key} type="button" onClick={() => setFrameColor(f)} className="rounded-full border px-3 py-1.5 font-mono text-[11px]" style={{ borderColor: on ? "var(--color-accent)" : "var(--color-border-default)", color: on ? "var(--color-accent)" : "rgba(255,255,255,.75)" }}>
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/65">Gallery wall</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={addFrame} disabled={frames.length >= 3} className="rounded-full border px-3 py-1.5 font-mono text-[11px] text-white/75 disabled:opacity-40" style={{ borderColor: "var(--color-border-default)" }}>+ Add a picture</button>
            <button type="button" onClick={removeFrame} disabled={frames.length <= 1} className="rounded-full border px-3 py-1.5 font-mono text-[11px] text-white/75 disabled:opacity-40" style={{ borderColor: "var(--color-border-default)" }}>Remove</button>
            <button type="button" onClick={shuffle} disabled={pool.length < 2} className="rounded-full border px-3 py-1.5 font-mono text-[11px] text-white/75 disabled:opacity-40" style={{ borderColor: "var(--color-border-default)" }}>Surprise me</button>
          </div>
        </div>
      </div>

      {/* Pick any picture */}
      <div className="mt-5">
        <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/65" htmlFor="room-search">Hang any picture in the library</label>
        <input
          id="room-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Try “husky”, “lion minimalist”, “nursery”…"
          className="mt-2 w-full rounded-xl border px-4 py-3 text-[14px] text-white outline-none focus:border-[color:var(--color-accent)]"
          style={{ borderColor: "var(--color-border-default)", background: "var(--color-bg-elev-1)" }}
        />
        {(results.length > 0 || pool.length > 1) && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {(results.length > 0 ? results : pool).map((a) => {
              const on = current?.art.slug === a.slug;
              return (
                <button key={a.slug} type="button" aria-label={a.title} title={a.title} onClick={() => setArt(a)} className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border" style={{ borderColor: on ? "var(--color-accent)" : "var(--color-border-default)" }}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={a.cover} alt="" loading="lazy" className="h-full w-full" style={{ objectFit: "cover" }} />
                </button>
              );
            })}
          </div>
        )}
        {searching && <p className="mt-2 text-[12px] text-white/65">Searching…</p>}
        {current && (
          <p className="mt-3 text-[12px] text-white/65">
            On the wall: <span className="text-white/80">{current.art.title}</span> at {current.size.label}.{" "}
            <a href={`/digital-art/${current.art.slug}`} className="underline underline-offset-4" style={{ color: "var(--color-accent)" }}>Open it →</a>
          </p>
        )}
        {/* Buy the wall — every distinct picture hanging here, as one order,
         * 20% off for two or more (the "gallery-wall" bundle in
         * /api/shop/checkout). Digital files; frames are the buyer's. */}
        {distinctSlugs.length >= 2 && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={buying}
              onClick={buyWall}
              className="rounded-full px-5 py-3 text-[14px] font-semibold text-white disabled:opacity-60"
              style={{ background: "var(--color-accent)" }}
            >
              {buying ? "Opening checkout…" : `Buy this wall — ${distinctSlugs.length} prints, 20% off`}
            </button>
            <span className="text-[12px] text-white/65">Digital files for every picture on the wall. Print at home or at any print shop.</span>
            {buyMsg && <span className="text-[12px] text-white/70">{buyMsg}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
